import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';
import LoadingOverlay from '../components/LoadingOverlay';
import './AppPage.css';
import './AdminCompaniesPage.css';

type Tenant = {
  id: string;
  businessName: string;
  cuit: string;
  email?: string;
  phone?: string;
  address?: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';
  maxEmployees: number;
  currentEmployeesCount: number;
  medicalCertificateRetentionDays: number;
  admin?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    status: string;
  };
};

type PreviewRow = { rowNumber: number; data: Record<string, any>; errors: string[] };
type CatalogKind = 'positions' | 'sectors' | 'branches';
type MissingCatalogs = Record<CatalogKind, string[]>;
type ImportPreview = {
  fileName: string;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  availableEmployees: number;
  missingCatalogs: MissingCatalogs;
  rows: PreviewRow[];
};

const emptyCatalogSelection: MissingCatalogs = { positions: [], sectors: [], branches: [] };

const emptyCompany = {
  businessName: '',
  cuit: '',
  email: '',
  phone: '',
  address: '',
  maxEmployees: 100,
  adminFirstName: '',
  adminLastName: '',
  adminEmail: '',
  temporaryPassword: '',
};

export default function AdminCompaniesPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(emptyCompany);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [adminPassword, setAdminPassword] = useState('');
  const [importTenant, setImportTenant] = useState<Tenant | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [catalogsToCreate, setCatalogsToCreate] =
    useState<MissingCatalogs>(emptyCatalogSelection);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const load = async () => {
    setBusy('Cargando empresas…');
    try {
      setTenants((await apiClient.get('/tenants')).data);
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudieron cargar las empresas.');
    } finally {
      setBusy('');
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const visible = useMemo(() => {
    const value = search.trim().toLowerCase();
    return value
      ? tenants.filter((tenant) =>
          `${tenant.businessName} ${tenant.cuit} ${tenant.email || ''}`
            .toLowerCase()
            .includes(value)
        )
      : tenants;
  }, [search, tenants]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy('Creando empresa…');
    setError('');
    setMessage('');
    try {
      await apiClient.post('/tenants', {
        businessName: form.businessName,
        cuit: form.cuit.replace(/\D/g, ''),
        email: form.email || undefined,
        phone: form.phone || undefined,
        address: form.address || undefined,
        maxEmployees: Number(form.maxEmployees),
        initialAdmin: {
          firstName: form.adminFirstName,
          lastName: form.adminLastName,
          email: form.adminEmail,
          temporaryPassword: form.temporaryPassword,
        },
      });
      setForm(emptyCompany);
      setShowCreate(false);
      setMessage('Empresa y administrador inicial creados correctamente.');
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo crear la empresa.');
    } finally {
      setBusy('');
    }
  };

  const update = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editing) return;
    setBusy('Actualizando empresa…');
    setError('');
    try {
      await apiClient.put(`/tenants/${editing.id}`, {
        businessName: editing.businessName,
        email: editing.email || undefined,
        phone: editing.phone || undefined,
        address: editing.address || undefined,
        status: editing.status,
        maxEmployees: Number(editing.maxEmployees),
        medicalCertificateRetentionDays: Number(editing.medicalCertificateRetentionDays),
        admin: editing.admin
          ? {
              firstName: editing.admin.firstName,
              lastName: editing.admin.lastName,
              email: editing.admin.email,
              password: adminPassword || undefined,
            }
          : undefined,
      });
      setEditing(null);
      setAdminPassword('');
      setMessage('Empresa actualizada.');
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo actualizar la empresa.');
    } finally {
      setBusy('');
    }
  };

  const toggleStatus = async (tenant: Tenant) => {
    const status = tenant.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
    if (!confirm(`${status === 'SUSPENDED' ? 'Suspender' : 'Reactivar'} ${tenant.businessName}?`))
      return;
    setBusy('Actualizando estado…');
    setError('');
    try {
      await apiClient.put(`/tenants/${tenant.id}`, { status });
      setMessage(status === 'ACTIVE' ? 'Empresa reactivada.' : 'Empresa suspendida.');
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo cambiar el estado.');
    } finally {
      setBusy('');
    }
  };

  const downloadTemplate = async () => {
    setBusy('Preparando plantilla…');
    try {
      const response = await apiClient.get('/tenants/employee-import/template', {
        responseType: 'blob',
      });
      downloadBlob(response.data, 'plantilla-empleados.xlsx');
    } catch {
      setError('No se pudo descargar la plantilla.');
    } finally {
      setBusy('');
    }
  };

  const previewFile = async (file: File) => {
    if (!importTenant) return;
    const data = new FormData();
    data.append('file', file);
    setBusy('Validando archivo…');
    setError('');
    setPreview(null);
    try {
      const result = (
        await apiClient.post(`/tenants/${importTenant.id}/employee-import/preview`, data)
      ).data as ImportPreview;
      setPreview(result);
      setCatalogsToCreate({
        positions: [...(result.missingCatalogs?.positions || [])],
        sectors: [...(result.missingCatalogs?.sectors || [])],
        branches: [...(result.missingCatalogs?.branches || [])],
      });
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo validar el archivo.');
    } finally {
      setBusy('');
    }
  };

  const commitImport = async () => {
    if (!importTenant || !preview || preview.invalidRows) return;
    setBusy('Importando empleados…');
    setError('');
    try {
      const result = (
        await apiClient.post(`/tenants/${importTenant.id}/employee-import`, {
          rows: preview.rows.map((row) => row.data),
          createCatalogs: catalogsToCreate,
        })
      ).data;
      setMessage(`${result.imported} empleados importados correctamente.`);
      setPreview(null);
      setCatalogsToCreate(emptyCatalogSelection);
      setImportTenant(null);
      await load();
    } catch (requestError: any) {
      setError(requestError.response?.data?.message || 'No se pudo completar la importación.');
    } finally {
      setBusy('');
    }
  };

  const downloadErrors = () => {
    if (!preview) return;
    const lines = [
      ['Fila', 'Errores'],
      ...preview.rows
        .filter((row) => row.errors.length)
        .map((row) => [String(row.rowNumber), row.errors.join(' | ')]),
    ].map((row) => row.map((value) => `"${value.replace(/"/g, '""')}"`).join(','));
    downloadBlob(
      new Blob([`\ufeff${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' }),
      'errores-importacion.csv'
    );
  };

  return (
    <div className="app-page admin-companies">
      {busy && <LoadingOverlay title={busy} />}
      <header className="app-bar">
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          ← Volver
        </button>
        <h1>Administración de empresas</h1>
        <button
          className="primary-button"
          onClick={() => {
            setShowCreate(true);
            setEditing(null);
          }}
        >
          Nueva empresa
        </button>
      </header>
      <main className="app-main">
        <div className="page-heading">
          <div>
            <span className="eyebrow">Superadministración</span>
            <h2>Empresas</h2>
            <p>Altas, cupos, estados e importación masiva de empleados.</p>
          </div>
          <div className="summary-pill">
            <strong>{tenants.length}</strong>
            <span>empresas</span>
          </div>
        </div>
        {error && (
          <div className="error-banner">
            {typeof error === 'string' ? error : JSON.stringify(error)}
          </div>
        )}
        {message && <div className="success-banner">{message}</div>}

        {showCreate && (
          <section className="panel">
            <div className="panel-heading">
              <div>
                <h2>Nueva empresa</h2>
                <p>Se creará también el primer usuario administrador.</p>
              </div>
            </div>
            <form className="form-grid" onSubmit={create}>
              <Field
                label="Razón social"
                value={form.businessName}
                onChange={(value) => setForm({ ...form, businessName: value })}
                required
              />
              <Field
                label="CUIT (11 dígitos)"
                value={form.cuit}
                onChange={(value) => setForm({ ...form, cuit: value })}
                required
                pattern="[0-9-]{11,13}"
              />
              <Field
                label="Email de la empresa"
                type="email"
                value={form.email}
                onChange={(value) => setForm({ ...form, email: value })}
              />
              <Field
                label="Teléfono"
                value={form.phone}
                onChange={(value) => setForm({ ...form, phone: value })}
              />
              <Field
                label="Dirección"
                value={form.address}
                onChange={(value) => setForm({ ...form, address: value })}
              />
              <Field
                label="Límite de empleados"
                type="number"
                min="1"
                value={String(form.maxEmployees)}
                onChange={(value) => setForm({ ...form, maxEmployees: Number(value) })}
                required
              />
              <h3>Administrador inicial</h3>
              <span />
              <Field
                label="Nombre"
                value={form.adminFirstName}
                onChange={(value) => setForm({ ...form, adminFirstName: value })}
                required
              />
              <Field
                label="Apellido"
                value={form.adminLastName}
                onChange={(value) => setForm({ ...form, adminLastName: value })}
                required
              />
              <Field
                label="Email de acceso"
                type="email"
                value={form.adminEmail}
                onChange={(value) => setForm({ ...form, adminEmail: value })}
                required
              />
              <Field
                label="Contraseña temporal"
                type="password"
                minLength={8}
                value={form.temporaryPassword}
                onChange={(value) => setForm({ ...form, temporaryPassword: value })}
                required
              />
              <div className="form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowCreate(false)}
                >
                  Cancelar
                </button>
                <button className="primary-button">Crear empresa</button>
              </div>
            </form>
          </section>
        )}

        {editing && (
          <section className="panel">
            <div className="panel-heading">
              <div>
                <h2>Editar {editing.businessName}</h2>
                <p>El límite nunca puede ser menor a la cantidad actualmente cargada.</p>
              </div>
            </div>
            <form className="form-grid" onSubmit={update}>
              <Field
                label="Razón social"
                value={editing.businessName}
                onChange={(businessName) => setEditing({ ...editing, businessName })}
                required
              />
              <Field
                label="Email"
                type="email"
                value={editing.email || ''}
                onChange={(email) => setEditing({ ...editing, email })}
              />
              <Field
                label="Teléfono"
                value={editing.phone || ''}
                onChange={(phone) => setEditing({ ...editing, phone })}
              />
              <Field
                label="Dirección"
                value={editing.address || ''}
                onChange={(address) => setEditing({ ...editing, address })}
              />
              <Field
                label="Límite de empleados"
                type="number"
                min={String(editing.currentEmployeesCount || 1)}
                value={String(editing.maxEmployees)}
                onChange={(value) => setEditing({ ...editing, maxEmployees: Number(value) })}
                required
              />
              <Field
                label="Conservación de certificados médicos (días)"
                type="number"
                min="30"
                max="36500"
                value={String(editing.medicalCertificateRetentionDays)}
                onChange={(value) =>
                  setEditing({ ...editing, medicalCertificateRetentionDays: Number(value) })
                }
                required
              />
              <label>
                Estado
                <select
                  value={editing.status}
                  onChange={(event) =>
                    setEditing({ ...editing, status: event.target.value as Tenant['status'] })
                  }
                >
                  <option value="ACTIVE">Activa</option>
                  <option value="SUSPENDED">Suspendida</option>
                  <option value="ARCHIVED">Archivada</option>
                </select>
              </label>
              {editing.admin ? (
                <>
                  <h3>Usuario administrador</h3>
                  <span />
                  <Field
                    label="Nombre"
                    value={editing.admin.firstName}
                    onChange={(firstName) =>
                      setEditing({
                        ...editing,
                        admin: { ...editing.admin!, firstName },
                      })
                    }
                    required
                  />
                  <Field
                    label="Apellido"
                    value={editing.admin.lastName}
                    onChange={(lastName) =>
                      setEditing({
                        ...editing,
                        admin: { ...editing.admin!, lastName },
                      })
                    }
                    required
                  />
                  <Field
                    label="Email de acceso"
                    type="email"
                    value={editing.admin.email}
                    onChange={(email) =>
                      setEditing({ ...editing, admin: { ...editing.admin!, email } })
                    }
                    required
                  />
                  <Field
                    label="Nueva contraseña (opcional)"
                    type="password"
                    minLength={8}
                    value={adminPassword}
                    onChange={setAdminPassword}
                    placeholder="Dejar vacío para conservar la actual"
                  />
                </>
              ) : (
                <p>Esta empresa no tiene un usuario administrador configurado.</p>
              )}
              <div className="form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => {
                    setEditing(null);
                    setAdminPassword('');
                  }}
                >
                  Cancelar
                </button>
                <button className="primary-button">Guardar cambios</button>
              </div>
            </form>
          </section>
        )}

        <section className="panel company-directory">
          <div className="panel-heading">
            <div>
              <h2>Directorio</h2>
              <p>El cupo considera empleados sin baja lógica.</p>
            </div>
            <input
              className="company-search"
              placeholder="Buscar empresa, CUIT o email"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Estado</th>
                  <th>Empleados</th>
                  <th>Uso del cupo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((tenant) => {
                  const percentage = Math.min(
                    100,
                    Math.round((tenant.currentEmployeesCount / tenant.maxEmployees) * 100)
                  );
                  return (
                    <tr key={tenant.id}>
                      <td>
                        <strong>{tenant.businessName}</strong>
                        <br />
                        <small>
                          CUIT {tenant.cuit} · {tenant.email || 'Sin email'}
                        </small>
                      </td>
                      <td>
                        <span className={`tenant-status ${tenant.status.toLowerCase()}`}>
                          {tenant.status === 'ACTIVE'
                            ? 'Activa'
                            : tenant.status === 'SUSPENDED'
                              ? 'Suspendida'
                              : 'Archivada'}
                        </span>
                      </td>
                      <td>
                        <strong>
                          {tenant.currentEmployeesCount} / {tenant.maxEmployees}
                        </strong>
                        <br />
                        <small>
                          {Math.max(0, tenant.maxEmployees - tenant.currentEmployeesCount)}{' '}
                          disponibles
                        </small>
                      </td>
                      <td>
                        <div className="quota">
                          <span style={{ width: `${percentage}%` }} />
                        </div>
                        <small>{percentage}% utilizado</small>
                      </td>
                      <td className="row-actions">
                        <button
                          className="secondary-button"
                          onClick={() => {
                            setEditing({
                              ...tenant,
                              admin: tenant.admin ? { ...tenant.admin } : undefined,
                            });
                            setAdminPassword('');
                            setShowCreate(false);
                          }}
                        >
                          Editar
                        </button>
                        <button
                          className="primary-button"
                          disabled={tenant.status !== 'ACTIVE'}
                          onClick={() => {
                            setImportTenant(tenant);
                            setPreview(null);
                          }}
                        >
                          Importar
                        </button>
                        <button
                          className={
                            tenant.status === 'ACTIVE' ? 'danger-button' : 'secondary-button'
                          }
                          onClick={() => void toggleStatus(tenant)}
                        >
                          {tenant.status === 'ACTIVE' ? 'Suspender' : 'Reactivar'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {importTenant && (
        <div
          className="import-overlay"
          onMouseDown={() => {
            setImportTenant(null);
            setPreview(null);
          }}
        >
          <section className="import-dialog" onMouseDown={(event) => event.stopPropagation()}>
            <header>
              <div>
                <span className="eyebrow">Carga masiva</span>
                <h2>{importTenant.businessName}</h2>
                <p>
                  {importTenant.currentEmployeesCount} de {importTenant.maxEmployees} empleados
                  cargados.
                </p>
              </div>
              <button
                aria-label="Cerrar"
                onClick={() => {
                  setImportTenant(null);
                  setPreview(null);
                }}
              >
                ×
              </button>
            </header>
            {!preview ? (
              <div className="import-start">
                <button className="secondary-button" onClick={() => void downloadTemplate()}>
                  Descargar plantilla XLSX
                </button>
                <label className="drop-zone">
                  <span className="drop-icon">XLSX</span>
                  <strong>Seleccionar archivo de empleados</strong>
                  <small>XLSX o CSV, hasta 5 MB y 2000 filas</small>
                  <input
                    type="file"
                    accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(event) =>
                      event.target.files?.[0] && void previewFile(event.target.files[0])
                    }
                  />
                </label>
              </div>
            ) : (
              <>
                <div className="import-summary">
                  <span>
                    <strong>{preview.totalRows}</strong>Total
                  </span>
                  <span className="valid">
                    <strong>{preview.validRows}</strong>Válidas
                  </span>
                  <span className={preview.invalidRows ? 'invalid' : 'valid'}>
                    <strong>{preview.invalidRows}</strong>Con errores
                  </span>
                  <span>
                    <strong>{preview.availableEmployees}</strong>Cupo disponible
                  </span>
                </div>
                {preview.invalidRows > 0 && (
                  <div className="error-banner">
                    Corregí las filas marcadas y volvé a seleccionar el archivo.{' '}
                    <button className="link-button" onClick={downloadErrors}>
                      Descargar errores CSV
                    </button>
                  </div>
                )}
                {(['positions', 'sectors', 'branches'] as CatalogKind[]).some(
                  (kind) => preview.missingCatalogs?.[kind]?.length
                ) && (
                  <section className="missing-catalogs">
                    <div>
                      <strong>Se encontraron datos que todavía no existen</strong>
                      <p>
                        Elegí cuáles querés crear. Los que dejes sin seleccionar se importarán sin
                        asignar.
                      </p>
                    </div>
                    {(['positions', 'sectors', 'branches'] as CatalogKind[]).map((kind) =>
                      preview.missingCatalogs?.[kind]?.length ? (
                        <fieldset key={kind}>
                          <legend>
                            {kind === 'positions'
                              ? 'Puestos'
                              : kind === 'sectors'
                                ? 'Sectores'
                                : 'Sucursales'}
                          </legend>
                          {preview.missingCatalogs[kind].map((name) => (
                            <label key={name}>
                              <input
                                type="checkbox"
                                checked={catalogsToCreate[kind].includes(name)}
                                onChange={(event) =>
                                  setCatalogsToCreate((current) => ({
                                    ...current,
                                    [kind]: event.target.checked
                                      ? [...current[kind], name]
                                      : current[kind].filter((item) => item !== name),
                                  }))
                                }
                              />
                              Crear “{name}”
                            </label>
                          ))}
                        </fieldset>
                      ) : null
                    )}
                  </section>
                )}
                <div className="table-wrap import-table">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Empleado</th>
                        <th>DNI / CUIL</th>
                        <th>Legajo</th>
                        <th>Resultado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((row) => (
                        <tr key={row.rowNumber} className={row.errors.length ? 'row-invalid' : ''}>
                          <td>{row.rowNumber}</td>
                          <td>
                            {row.data.firstName} {row.data.lastName}
                          </td>
                          <td>
                            {row.data.dni}
                            <br />
                            <small>{row.data.cuil}</small>
                          </td>
                          <td>{row.data.legajo}</td>
                          <td>
                            {row.errors.length ? (
                              row.errors.map((item) => (
                                <small className="row-error" key={item}>
                                  {item}
                                </small>
                              ))
                            ) : (
                              <span className="tenant-status active">Lista para importar</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="import-actions">
                  <button
                    className="secondary-button"
                    onClick={() => {
                      setPreview(null);
                      setCatalogsToCreate(emptyCatalogSelection);
                    }}
                  >
                    Elegir otro archivo
                  </button>
                  <button
                    className="primary-button"
                    disabled={preview.invalidRows > 0 || preview.validRows === 0}
                    onClick={() => void commitImport()}
                  >
                    Importar {preview.validRows} empleados
                  </button>
                </div>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  onChange,
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
  label: string;
  onChange: (value: string) => void;
}) {
  return (
    <label>
      {label}
      <input {...props} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function downloadBlob(value: Blob, fileName: string) {
  const url = URL.createObjectURL(value);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}
